import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpEventType, HttpHeaderResponse, HttpResponse } from '@angular/common/http';
import { scan, concatMap, tap, finalize, catchError, switchMap, takeUntil } from 'rxjs/operators';
import { of, from, Subject, Observable } from 'rxjs';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { FileOpener } from '@capacitor-community/file-opener';
import * as i2 from '@ngx-translate/core';
import * as i4 from '@ionic/angular/standalone';
import { IonicSafeString } from '@ionic/angular/standalone';
import * as i2$1 from '@angular/router';

var FileEventTypes;
(function (FileEventTypes) {
    FileEventTypes["Canceled"] = "canceled";
    FileEventTypes["Completed"] = "completed";
    FileEventTypes["Failed"] = "failed";
})(FileEventTypes || (FileEventTypes = {}));
var FileUploadingErrorType;
(function (FileUploadingErrorType) {
    FileUploadingErrorType["Size"] = "size";
    FileUploadingErrorType["Type"] = "type";
    FileUploadingErrorType["Unknown"] = "unknown";
})(FileUploadingErrorType || (FileUploadingErrorType = {}));
var AlertContentType;
(function (AlertContentType) {
    AlertContentType["Progress"] = "progress";
    AlertContentType["Preview"] = "preview";
    AlertContentType["Error"] = "error";
})(AlertContentType || (AlertContentType = {}));
var FileFormatTypes;
(function (FileFormatTypes) {
    FileFormatTypes["Application"] = "application";
    FileFormatTypes["Image"] = "image";
    FileFormatTypes["Multimedia"] = "multimedia";
    FileFormatTypes["Text"] = "text";
    FileFormatTypes["Incorrect"] = "incorrect";
})(FileFormatTypes || (FileFormatTypes = {}));

function isHttpResponse(event, type = 'upload') {
    // console.log('isHttpResponse', event, type);
    return type === 'upload' ? ((event.type === HttpEventType.Response || event.type === HttpEventType.ResponseHeader)
        && event instanceof HttpHeaderResponse)
        : event.type === HttpEventType.Response && event instanceof HttpResponse;
}
function isHttpProgressEvent(event, type = 'upload') {
    return type === 'upload' ? (event.type === HttpEventType.DownloadProgress && !!event.total || event.type === HttpEventType.UploadProgress) : (event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.UploadProgress);
}
function upload() {
    const initialState = { state: 'PENDING', progress: 0 };
    const calculateState = (
    // tslint:disable-next-line:no-shadowed-variable
    upload, event) => {
        return {
            state: 'DONE',
            progress: 100
        };
        // implementation
    };
    return (source) => source.pipe(scan(calculateState, initialState));
}
function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
function isBytesValid(bytes) {
    return bytes >= 10;
}

class BaseAlertService {
    ac;
    alert;
    constructor(ac) {
        this.ac = ac;
    }
    get getAlert() {
        return this.alert;
    }
    createAlertWithCustomContent(options) {
        return of([])
            .pipe(concatMap(() => this.alert ? from(this.alert.dismiss()) : of(null)), concatMap(() => {
            return from(this.ac.create(options))
                .pipe(tap((c) => {
                this.alert = c;
                console.log('setAlert', c);
            }));
        }), finalize(() => {
            console.log('finalize');
        }));
    }
    createButton(text, handler) {
        return {
            text,
            handler
        };
    }
    dismiss(data) {
        // return fromPromise(this.alert.dismiss(data));
        return from(this.ac.dismiss(data));
    }
    onWillDismiss() {
        return from(this.alert.onWillDismiss());
    }
    onDidDismiss() {
        return from(this.alert.onDidDismiss());
    }
    update(options) {
        if (options?.message) {
            this.alert.message = new IonicSafeString(options.message);
        }
        if (options?.buttons) {
            this.alert.buttons = options.buttons;
        }
        if (options?.inputs) {
            this.alert.inputs = [...options.inputs];
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: BaseAlertService, deps: [{ token: i4.AlertController }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: BaseAlertService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: BaseAlertService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i4.AlertController }] });

class FileUploaderBase {
    http;
    translateService;
    alertService;
    plt;
    API_URL;
    fileNameToUpload;
    fileToUpload = null;
    notifier = new Subject();
    filePreview;
    uploadProgress;
    MAX_FILE_SIZE = 30_000_000;
    MAX_FILE_SIZE_READABLE = this.MAX_FILE_SIZE / 1_000_000;
    fileTypes;
    storage = Preferences;
    uploadingSubject = new Subject();
    $uploadingObs;
    _fileError;
    constructor(http, translateService, alertService, 
    // @Inject(ENVIRONMENT) protected env: Environment,
    plt) {
        this.http = http;
        this.translateService = translateService;
        this.alertService = alertService;
        this.plt = plt;
        this.$uploadingObs = this.uploadingSubject.asObservable();
        const b = 'api-url';
        this.getItem(b).then(async (v) => {
            const webLink = await this.getItem('app_api-url');
            if (webLink) {
                this.API_URL = webLink;
            }
            else {
                if (!v?.includes('https://')) {
                    this.API_URL = 'https://live.v-locker.ch';
                }
                else {
                    this.API_URL = v || webLink;
                }
            }
            console.log('FileUploaderBase	Service', this.API_URL);
        }).catch(() => {
            this.API_URL = 'https://live.v-locker.ch';
        });
    }
    async getItem(key) {
        try {
            const { value } = await this.storage.get({ key });
            return JSON.parse(value);
        }
        catch (error) {
            return null;
        }
    }
    get(url, data = null, type = 'json', progress = false) {
        // console.log('get - url', url);
        const headers = this.setHeaders(url, type === 'json' ? 'application/json' : 'multipart/form-data');
        let options = {
            headers,
            responseType: type === 'file' ? 'blob' : 'json'
        };
        if (progress) {
            options = {
                ...options,
                ...{
                    reportProgress: true,
                    observe: 'events',
                }
            };
        }
        if (data) {
            return this.http.post(`${this.API_URL}/net/zoho/get/`, {
                params: data
            }, options).pipe(tap((r) => {
            }));
        }
        else {
            return this.http.post(`${this.API_URL}/net/zoho/get/?u${url}`, {}, options).pipe(tap((r) => {
            }), catchError((err) => {
                console.log(url);
                return of(err);
            }));
        }
    }
    getChatFile(fileUrl) {
        fileUrl = fileUrl.replace('http://', 'https://');
        console.log('getChatFile', fileUrl);
        return this.http.get(fileUrl, {
            reportProgress: true,
            responseType: 'blob',
            observe: 'events',
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }
    addFile($event, type = AlertContentType.Progress) {
        const file = $event.target.files[0];
        console.log('$event.target', file);
        if (!file) {
            return;
        }
        const validateFile = this.validateFile(file);
        type = validateFile.fileType ? type : AlertContentType.Error;
        // console.log('validateFile', type, validateFile.error);
        return of({ file, validateFile, type });
    }
    doPreview(file, validateFile, type) {
        const _doPreview = (toastType) => {
            return Observable.create((sub) => {
                const r = this.getFileReader();
                // if success
                r.onload = (ev) => {
                    const fileResult = ev.target.result;
                    this.filePreview = fileResult;
                    sub.next(fileResult);
                };
                // if failed
                r.onerror = (ev) => {
                    sub.error(ev);
                };
                r.readAsDataURL(file);
            });
        };
        switch (type) {
            case AlertContentType.Preview:
                return _doPreview(AlertContentType.Preview);
            case AlertContentType.Progress:
            case AlertContentType.Error:
                return _doPreview(AlertContentType.Error);
        }
    }
    presentAlertForPreview(file, validateFile, type, options) {
        console.log('presentAlertForPreview', file.size, options);
        const _innerOptions = {
            total: formatBytes(file.size),
            errorType: validateFile.error,
        };
        return this.presentAlert(options ? {
            ..._innerOptions,
            ...options
        } : {
            total: formatBytes(file.size),
            errorType: validateFile.error,
        }, type, validateFile.fileType);
    }
    handleErrorsOnAddingFile(validateFile, file) {
        if (validateFile.error === FileUploadingErrorType.Size) {
            this._fileError = this.translateService.instant('file-uploader.errors.size');
        }
        else if (validateFile.error === FileUploadingErrorType.Type) {
            this._fileError = this.translateService.instant('file-uploader.errors.type');
        }
        else {
            this.fileToUpload = file;
            this.fileNameToUpload = this.fileToUpload.name;
            this._fileError = null;
        }
        return of(1);
    }
    presentAlert(options, alertContentType = AlertContentType.Progress, fileType) {
        // console.log('presentAlert-1', options);
        const { errorType, yourLogic } = options;
        const html = this.chooseHtmlTemplate(errorType ? AlertContentType.Error : alertContentType, {
            ...options,
            ...{ fileType }
        });
        // alertContentType = FileHtmlType.Error as any;
        // errorType = FileUploadingErrorType.Unknown;
        const uploadHandler = {
            text: this.translateService.instant('file-uploader.upload'),
            handler: () => {
                const updatedHtml = this.chooseHtmlTemplate(AlertContentType.Progress, {
                    total: formatBytes(this.fileToUpload.size),
                    progress: 1
                });
                this.alertService.update({
                    message: updatedHtml,
                    buttons: [cancelHandler]
                });
                this.notifier = new Subject();
                if (options.uploadHandlerFunc) {
                    options.uploadHandlerFunc();
                }
                return false;
            }
        };
        const cancelHandler = {
            text: this.translateService.instant('buttons.cancel'),
            handler: () => {
                this.notifier.next('');
                this.notifier.complete();
                this.dismissAlert(FileEventTypes.Canceled);
                return false;
            }
        };
        const buttons = [];
        if (errorType) {
            buttons.push(cancelHandler);
        }
        else if (alertContentType === AlertContentType.Preview) {
            buttons.push(uploadHandler);
            buttons.push(cancelHandler);
        }
        else {
            buttons.push(cancelHandler);
        }
        return this.alertService.createAlertWithCustomContent({
            // this.fileAlertLoader = await this.alertController.create({
            cssClass: 'file-uploader-toast',
            message: html,
            backdropDismiss: false,
            buttons
        }).pipe(tap((c) => {
            if (alertContentType === AlertContentType.Preview) {
                this.showVideoPreview(fileType);
            }
            if (!errorType) {
                this.alertService.onDidDismiss()
                    // fromPromise(this.fileAlertLoader.onDidDismiss())
                    .subscribe((data) => {
                    return this.onDismissAlertAndClear(data, 'upload');
                });
            }
            c.present();
            if (yourLogic) {
                yourLogic();
            }
        }));
    }
    showVideoPreview(fileType) {
        if (fileType === FileFormatTypes.Multimedia) {
            const video = document.getElementById('videoPreview');
            const blobURL = URL.createObjectURL(this.fileToUpload);
            video['controls'] = true;
            video.style.width = '100%';
            video['src'] = blobURL;
        }
    }
    updateAlertContent(fileHtmlType, options) {
        const html = this.chooseHtmlTemplate(fileHtmlType, options);
        this.alertService.update({
            message: html
        });
    }
    onDismissAlertAndClear(res, reqType) {
        this.clearFile();
        // Notify subscriber that alert was dismissed
        if (res.data !== FileEventTypes.Completed && res.data !== FileEventTypes.Failed) {
            this.uploadingSubject.next({ reqType, fileEventTypes: FileEventTypes.Canceled });
        }
    }
    dismissAlert(type = FileEventTypes.Completed) {
        return this.alertService.dismiss(type)
            .pipe(tap(() => this.clearFile()));
    }
    chooseHtmlTemplate(fileHtmlType, options) {
        let template;
        switch (fileHtmlType) {
            case AlertContentType.Progress:
                template = this.progressHtml(options.loaded, options.total, options.progress);
                break;
            case AlertContentType.Preview:
                template = this.previewHtml(options.total, options.fileType);
                break;
            case AlertContentType.Error:
                template = this.errorHtml(options.errorType);
                break;
        }
        return template;
    }
    progressHtml(loaded, total, progress) {
        const html = `<div>
<p>${this.translateService.instant('file-uploader.uploading')} ${this.fileNameToUpload}</p>
                    <div class=''>${loaded || ''}
                        /${total || ''}</div>
                    <div class='progress'>
                    <div class='progress-bar'
                    role='progressbar'
                     style='width: ${progress}%'
                     aria-valuenow='50'
                     aria-valuemin='0'
                     aria-valuemax='100'></div>
                    </div>
</div>`;
        return html;
    }
    previewHtml(total, fileType) {
        let source = '';
        switch (fileType) {
            case FileFormatTypes.Image:
                source = `<img src='${this.filePreview}'>`;
                break;
            case FileFormatTypes.Multimedia:
                source = `<video width='320' height='240' id='videoPreview'></video>`;
                break;
            case FileFormatTypes.Application:
                source = `<ion-icon name='document-text-outline'></ion-icon>`;
                break;
            case FileFormatTypes.Text:
                source = `<ion-icon name='document-text-outline'></ion-icon>`;
                break;
        }
        // const href = 'https://ih1.redbubble.net/image.3376326747.2717/raf,128x128,075,f,101010:01c5ca27c6.jpg'
        const html = `<div><p>${this.translateService.instant('file-uploader.uploading')}
${this.fileNameToUpload}</p><div class=''>${total}</div>
                 <div class='preview'>
               ${source}
            </div>
</div>`;
        return html;
    }
    errorHtml(fileUploadingErrorType) {
        let errorMessage;
        switch (fileUploadingErrorType) {
            case FileUploadingErrorType.Size:
                errorMessage = `${this.translateService.instant('file-uploader.errors.size')} ${this.MAX_FILE_SIZE_READABLE}mb`;
                break;
            case FileUploadingErrorType.Type:
                errorMessage = this.translateService.instant('file-uploader.errors.type');
                break;
            case FileUploadingErrorType.Unknown:
                errorMessage = this.translateService.instant('file-uploader.errors.unknown');
                break;
            default:
                errorMessage = this.translateService.instant('file-uploader.errors.unknown');
                break;
        }
        return `<div><div class="alert-block error"><small>${errorMessage}</small></div></div>`;
    }
    getInitialState(totalSize = null) {
        return totalSize ? { state: 'PENDING', progress: 1, loaded: '0', total: totalSize } :
            { state: 'PENDING', progress: 1, loaded: '0' };
    }
    setHeaders(url, type) {
        const customHeaders = {};
        const headerUrl = 'Zoho-Forward-URL';
        const headerContentType = 'Content-Type';
        if (type === 'application/json') {
            customHeaders[headerContentType] = type;
        }
        customHeaders[headerUrl] = url;
        return customHeaders;
    }
    post(url, body, type = 'json', progress = false) {
        const headers = this.setHeaders(url, type === 'json' ? 'application/json' : 'multipart/form-data');
        // console.log('body', body);
        let options = {
            headers
        };
        if (progress) {
            options = {
                ...options,
                ...{
                    reportProgress: true,
                    observe: 'events',
                }
            };
        }
        return this.http.post(`${this.API_URL}/net/zoho/post/`, body, options);
    }
    downloadPdfANdSave(pdfBase64, fileName, fileExtantion, onSuccess) {
        if (this.plt.is('cordova')) {
            // Save the PDF to the device
            try {
                Filesystem.writeFile({
                    path: fileName,
                    data: pdfBase64,
                    directory: Directory.Documents
                    // encoding: FilesystemEncoding.UTF8
                }).then((writeFileResult) => {
                    Filesystem.getUri({
                        directory: Directory.Documents,
                        path: fileName
                    }).then((getUriResult) => {
                        const path = getUriResult.uri;
                        onSuccess(path);
                        FileOpener.open({ filePath: path, contentType: fileExtantion });
                    }, (error) => {
                        console.log(error);
                    });
                });
            }
            catch (error) {
                console.error('Unable to write file', error);
            }
        }
        else {
            // On a browser simply use download
            // this.pdfObj.download();
            onSuccess();
        }
    }
    downloadFile(file, fileName, fileExtantion, isMobile = true) {
        const newBlob = new Blob([file], { type: fileExtantion });
        if (isMobile) {
            const reader = this.getFileReader();
            reader.onloadend = () => {
                const baseUrl = reader.result;
                this.downloadPdfANdSave(baseUrl, fileName, fileExtantion, (data) => {
                    console.log('success!');
                });
            };
            reader.readAsDataURL(newBlob);
        }
        else {
            const windowReference = window.open("about:blank", "_blank");
            windowReference.document.write('Loading...');
            const url = window.URL.createObjectURL(newBlob);
            windowReference.location = url;
        }
    }
    getSingleAttachment(name, size, fileUrl, type = 'claims') {
        this.fileNameToUpload = name;
        const totalFileSize = formatBytes(Number(size));
        return this.presentAlert({
            loaded: '0',
            total: totalFileSize,
            progress: 1
        }, AlertContentType.Progress)
            .pipe(switchMap(() => {
            return type === 'claims' ? this.get(fileUrl, null, 'file', true) : this.getChatFile(fileUrl);
            // return this.get(
            // 	fileUrl,
            // 	null,
            // 	'file',
            // 	true);
        }), takeUntil(this.notifier), scan((u, e) => {
            return this.calcState(u, e, totalFileSize);
        }, this.getInitialState(totalFileSize)), tap((upload) => {
            this.uploadProgress = upload;
            // console.log('this.uploadProgress', this.uploadProgress);
            if (this?.uploadProgress?.state === 'DONE') {
                // console.log('uploadFileZoho - upload', this.uploadProgress);
                this.uploadingSubject.next({ reqType: 'download', fileEventTypes: FileEventTypes.Completed });
                // this.unsubscribe();
            }
            else if (upload.progress) {
                this.updateAlertContent(AlertContentType.Progress, {
                    loaded: upload.loaded,
                    total: upload.total,
                    progress: upload.progress
                });
            }
        }));
    }
    uploadFile(url) {
        if (!url) {
            throw new Error('a url here is required');
        }
        if (!this.fileToUpload) {
            return of([]);
        }
        const formData = new FormData();
        formData.append('file', this.fileToUpload);
        formData.append('title', this.fileToUpload.name);
        return this.http.post(url, formData, {
            reportProgress: true,
            observe: 'events',
        }).pipe(takeUntil(this.notifier), scan(this.calcState, this.getInitialState()));
    }
    calcState(upload, event, fileSize) {
        // console.log('calcState', event, upload);
        if (isHttpResponse(event, fileSize ? 'download' : 'upload')) {
            return {
                progress: 100,
                state: 'DONE',
                total: '',
                loaded: '',
                data: event.body || event
            };
        }
        if (event.type === HttpEventType.ResponseHeader && !event['ok']) {
            return {
                progress: 0,
                state: 'FAILED'
            };
        }
        if (isHttpProgressEvent(event, fileSize ? 'download' : 'upload')) {
            const totalSize = isBytesValid(event.total) ? event.total : fileSize;
            const formattedTotalSize = isBytesValid(event.total) ? formatBytes(event.total) : formatBytes(totalSize);
            // console.log('event', event);
            // console.log('totalSize', formattedTotalSize, event.loaded ? formatBytes(event.loaded) : '');
            return {
                progress: totalSize
                    ? Math.round((100 * event.loaded) / totalSize)
                    : upload.progress,
                state: 'IN_PROGRESS',
                total: formattedTotalSize,
                loaded: isBytesValid(event.loaded) ? formatBytes(event.loaded) : '0'
            };
        }
        // console.log('returnUpload', upload);
        return upload;
    }
    getFileReader() {
        const fileReader = new FileReader();
        const zoneOriginalInstance = fileReader['__zone_symbol__originalInstance'];
        return zoneOriginalInstance || fileReader;
    }
    findMemType(ext) {
        const memtypes = this.getMimtypes();
        return memtypes['.' + ext];
    }
    findFileType(file) {
        const fileType = file.type;
        if (fileType.includes('video') || fileType.includes('audio')) {
            const isMultimedia = this.fileTypes.multimedia.find(fv => fileType.includes(fv.mimeType));
            return isMultimedia ? FileFormatTypes.Multimedia : FileFormatTypes.Incorrect;
        }
        else if (fileType.includes(FileFormatTypes.Image)) {
            const isImage = this.fileTypes.image.find(fv => fileType.includes(fv.mimeType));
            return isImage ? FileFormatTypes.Image : FileFormatTypes.Incorrect;
        }
        else if (fileType.includes(FileFormatTypes.Application)) {
            const isFile = this.fileTypes.application.find(fv => fileType.includes(fv.mimeType));
            return isFile ? FileFormatTypes.Application : FileFormatTypes.Incorrect;
        }
        else if (fileType.includes(FileFormatTypes.Text)) {
            const isText = this.fileTypes.text.find(fv => fileType.includes(fv.mimeType));
            return isText ? FileFormatTypes.Text : FileFormatTypes.Incorrect;
        }
        return FileFormatTypes.Incorrect;
    }
    setMimeTypes(fileTypes) {
        const mimtypes = this.getMimtypes();
        const multimedia = [];
        const application = [];
        const images = [];
        const text = [];
        fileTypes.multimedia.forEach(v => {
            const ext = '.' + v;
            const mimtype = mimtypes[ext];
            if (mimtype) {
                multimedia.push({ extention: ext, mimeType: mimtype });
            }
        });
        fileTypes.image.forEach(v => {
            const ext = '.' + v;
            const mimtype = mimtypes[ext];
            if (mimtype) {
                images.push({ extention: ext, mimeType: mimtype });
            }
        });
        fileTypes.text.forEach(v => {
            const ext = '.' + v;
            const mimtype = mimtypes[ext];
            if (mimtype) {
                text.push({ extention: ext, mimeType: mimtype });
            }
        });
        fileTypes.application.forEach(v => {
            const ext = '.' + v;
            const mimtype = mimtypes[ext];
            if (mimtype) {
                application.push({ extention: ext, mimeType: mimtype });
            }
        });
        const res = {
            multimedia,
            text,
            image: images,
            application
        };
        return res;
    }
    fillInFileExtentions() {
        const innerList = {
            [FileFormatTypes.Multimedia]: [
                '3g2', '3gp', 'aaf', 'asf', 'avchd', 'avi', 'drc', 'flv', 'm2v', 'm3u8', 'm4p', 'm4v',
                'mkv', 'mng', 'mov', 'mp2', 'mp4', 'mpe', 'mpeg', 'mpg', 'mpv', 'mxf', 'nsv', 'ogg', 'ogv', 'qt', 'rm',
                'rmvb', 'roq', 'svi', 'vob', 'webm', 'wmv', 'yuv', 'quicktime'
            ],
            [FileFormatTypes.Application]: ['doc', 'xls', 'xlsx', 'pdf', 'docx'],
            [FileFormatTypes.Text]: ['txt', 'rtf'],
            [FileFormatTypes.Image]: ['jpg', 'jpeg', 'png', 'gif']
        };
        this.fileTypes = this.setMimeTypes(innerList);
    }
    unsubscribe() {
        this.notifier.next('');
        this.notifier.complete();
    }
    clearFile() {
        this.fileToUpload = null;
        this.filePreview = null;
        this.fileNameToUpload = null;
    }
    validateFile(file) {
        const fileSize = file.size;
        const result = {
            fileType: null,
            error: null
        };
        const loadedFileType = this.findFileType(file);
        if (loadedFileType === FileFormatTypes.Incorrect) {
            result.error = FileUploadingErrorType.Type;
        }
        else if (fileSize >= this.MAX_FILE_SIZE) {
            result.error = FileUploadingErrorType.Size;
        }
        else {
            result.fileType = loadedFileType;
        }
        return result;
    }
    onFileUploadError() {
        this.updateAlertContent(AlertContentType.Error, {
            errorType: FileUploadingErrorType.Unknown
        });
    }
    preloadLoadingData({ name, size }) {
        return of({ name, total: formatBytes(size) });
    }
    getMimtypes() {
        return {
            '.123': 'application/vnd.lotus-1-2-3',
            '.3dml': 'text/vnd.in3d.3dml',
            '.3g2': 'video/3gpp2',
            '.3gp': 'video/3gpp',
            '.a': 'application/octet-stream',
            '.aab': 'application/x-authorware-bin',
            '.aac': 'audio/x-aac',
            '.aam': 'application/x-authorware-map',
            '.aas': 'application/x-authorware-seg',
            '.abw': 'application/x-abiword',
            '.acc': 'application/vnd.americandynamics.acc',
            '.ace': 'application/x-ace-compressed',
            '.acu': 'application/vnd.acucobol',
            '.acutc': 'application/vnd.acucorp',
            '.adp': 'audio/adpcm',
            '.aep': 'application/vnd.audiograph',
            '.afm': 'application/x-font-type1',
            '.afp': 'application/vnd.ibm.modcap',
            '.ai': 'application/postscript',
            '.aif': 'audio/x-aiff',
            '.aifc': 'audio/x-aiff',
            '.aiff': 'audio/x-aiff',
            '.air': 'application/vnd.adobe.air-application-installer-package+zip',
            '.ami': 'application/vnd.amiga.ami',
            '.apk': 'application/vnd.android.package-archive',
            '.application': 'application/x-ms-application',
            '.apr': 'application/vnd.lotus-approach',
            '.asc': 'application/pgp-signature',
            '.asf': 'video/x-ms-asf',
            '.asm': 'text/x-asm',
            '.aso': 'application/vnd.accpac.simply.aso',
            '.asx': 'video/x-ms-asf',
            '.atc': 'application/vnd.acucorp',
            '.atom': 'application/atom+xml',
            '.atomcat': 'application/atomcat+xml',
            '.atomsvc': 'application/atomsvc+xml',
            '.atx': 'application/vnd.antix.game-component',
            '.au': 'audio/basic',
            '.avi': 'video/x-msvideo',
            '.aw': 'application/applixware',
            '.azf': 'application/vnd.airzip.filesecure.azf',
            '.azs': 'application/vnd.airzip.filesecure.azs',
            '.azw': 'application/vnd.amazon.ebook',
            '.bat': 'application/x-msdownload',
            '.bcpio': 'application/x-bcpio',
            '.bdf': 'application/x-font-bdf',
            '.bdm': 'application/vnd.syncml.dm+wbxml',
            '.bh2': 'application/vnd.fujitsu.oasysprs',
            '.bin': 'application/octet-stream',
            '.bmi': 'application/vnd.bmi',
            '.bmp': 'image/bmp',
            '.book': 'application/vnd.framemaker',
            '.box': 'application/vnd.previewsystems.box',
            '.boz': 'application/x-bzip2',
            '.bpk': 'application/octet-stream',
            '.btif': 'image/prs.btif',
            '.bz': 'application/x-bzip',
            '.bz2': 'application/x-bzip2',
            '.c': 'text/x-c',
            '.c4d': 'application/vnd.clonk.c4group',
            '.c4f': 'application/vnd.clonk.c4group',
            '.c4g': 'application/vnd.clonk.c4group',
            '.c4p': 'application/vnd.clonk.c4group',
            '.c4u': 'application/vnd.clonk.c4group',
            '.cab': 'application/vnd.ms-cab-compressed',
            '.car': 'application/vnd.curl.car',
            '.cat': 'application/vnd.ms-pki.seccat',
            '.cc': 'text/x-c',
            '.cct': 'application/x-director',
            '.ccxml': 'application/ccxml+xml',
            '.cdbcmsg': 'application/vnd.contact.cmsg',
            '.cdf': 'application/x-netcdf',
            '.cdkey': 'application/vnd.mediastation.cdkey',
            '.cdx': 'chemical/x-cdx',
            '.cdxml': 'application/vnd.chemdraw+xml',
            '.cdy': 'application/vnd.cinderella',
            '.cer': 'application/pkix-cert',
            '.cgm': 'image/cgm',
            '.chat': 'application/x-chat',
            '.chm': 'application/vnd.ms-htmlhelp',
            '.chrt': 'application/vnd.kde.kchart',
            '.cif': 'chemical/x-cif',
            '.cii': 'application/vnd.anser-web-certificate-issue-initiation',
            '.cil': 'application/vnd.ms-artgalry',
            '.cla': 'application/vnd.claymore',
            '.class': 'application/java-vm',
            '.clkk': 'application/vnd.crick.clicker.keyboard',
            '.clkp': 'application/vnd.crick.clicker.palette',
            '.clkt': 'application/vnd.crick.clicker.template',
            '.clkw': 'application/vnd.crick.clicker.wordbank',
            '.clkx': 'application/vnd.crick.clicker',
            '.clp': 'application/x-msclip',
            '.cmc': 'application/vnd.cosmocaller',
            '.cmdf': 'chemical/x-cmdf',
            '.cml': 'chemical/x-cml',
            '.cmp': 'application/vnd.yellowriver-custom-menu',
            '.cmx': 'image/x-cmx',
            '.cod': 'application/vnd.rim.cod',
            '.com': 'application/x-msdownload',
            '.conf': 'text/plain',
            '.cpio': 'application/x-cpio',
            '.cpp': 'text/x-c',
            '.cpt': 'application/mac-compactpro',
            '.crd': 'application/x-mscardfile',
            '.crl': 'application/pkix-crl',
            '.crt': 'application/x-x509-ca-cert',
            '.csh': 'application/x-csh',
            '.csml': 'chemical/x-csml',
            '.csp': 'application/vnd.commonspace',
            '.css': 'text/css',
            '.cst': 'application/x-director',
            '.csv': 'text/csv',
            '.cu': 'application/cu-seeme',
            '.curl': 'text/vnd.curl',
            '.cww': 'application/prs.cww',
            '.cxt': 'application/x-director',
            '.cxx': 'text/x-c',
            '.daf': 'application/vnd.mobius.daf',
            '.dataless': 'application/vnd.fdsn.seed',
            '.davmount': 'application/davmount+xml',
            '.dcr': 'application/x-director',
            '.dcurl': 'text/vnd.curl.dcurl',
            '.dd2': 'application/vnd.oma.dd2+xml',
            '.ddd': 'application/vnd.fujixerox.ddd',
            '.deb': 'application/x-debian-package',
            '.def': 'text/plain',
            '.deploy': 'application/octet-stream',
            '.der': 'application/x-x509-ca-cert',
            '.dfac': 'application/vnd.dreamfactory',
            '.dic': 'text/x-c',
            '.diff': 'text/plain',
            '.dir': 'application/x-director',
            '.dis': 'application/vnd.mobius.dis',
            '.dist': 'application/octet-stream',
            '.distz': 'application/octet-stream',
            '.djv': 'image/vnd.djvu',
            '.djvu': 'image/vnd.djvu',
            '.dll': 'application/x-msdownload',
            '.dmg': 'application/octet-stream',
            '.dms': 'application/octet-stream',
            '.dna': 'application/vnd.dna',
            '.doc': 'application/msword',
            '.docm': 'application/vnd.ms-word.document.macroenabled.12',
            '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            '.dot': 'application/msword',
            '.dotm': 'application/vnd.ms-word.template.macroenabled.12',
            '.dotx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
            '.dp': 'application/vnd.osgi.dp',
            '.dpg': 'application/vnd.dpgraph',
            '.dsc': 'text/prs.lines.tag',
            '.dtb': 'application/x-dtbook+xml',
            '.dtd': 'application/xml-dtd',
            '.dts': 'audio/vnd.dts',
            '.dtshd': 'audio/vnd.dts.hd',
            '.dump': 'application/octet-stream',
            '.dvi': 'application/x-dvi',
            '.dwf': 'model/vnd.dwf',
            '.dwg': 'image/vnd.dwg',
            '.dxf': 'image/vnd.dxf',
            '.dxp': 'application/vnd.spotfire.dxp',
            '.dxr': 'application/x-director',
            '.ecelp4800': 'audio/vnd.nuera.ecelp4800',
            '.ecelp7470': 'audio/vnd.nuera.ecelp7470',
            '.ecelp9600': 'audio/vnd.nuera.ecelp9600',
            '.ecma': 'application/ecmascript',
            '.edm': 'application/vnd.novadigm.edm',
            '.edx': 'application/vnd.novadigm.edx',
            '.efif': 'application/vnd.picsel',
            '.ei6': 'application/vnd.pg.osasli',
            '.elc': 'application/octet-stream',
            '.eml': 'message/rfc822',
            '.emma': 'application/emma+xml',
            '.eol': 'audio/vnd.digital-winds',
            '.eot': 'application/vnd.ms-fontobject',
            '.eps': 'application/postscript',
            '.epub': 'application/epub+zip',
            '.es3': 'application/vnd.eszigno3+xml',
            '.esf': 'application/vnd.epson.esf',
            '.et3': 'application/vnd.eszigno3+xml',
            '.etx': 'text/x-setext',
            '.exe': 'application/x-msdownload',
            '.ext': 'application/vnd.novadigm.ext',
            '.ez': 'application/andrew-inset',
            '.ez2': 'application/vnd.ezpix-album',
            '.ez3': 'application/vnd.ezpix-package',
            '.f': 'text/x-fortran',
            '.f4v': 'video/x-f4v',
            '.f77': 'text/x-fortran',
            '.f90': 'text/x-fortran',
            '.fbs': 'image/vnd.fastbidsheet',
            '.fdf': 'application/vnd.fdf',
            '.fe_launch': 'application/vnd.denovo.fcselayout-link',
            '.fg5': 'application/vnd.fujitsu.oasysgp',
            '.fgd': 'application/x-director',
            '.fh': 'image/x-freehand',
            '.fh4': 'image/x-freehand',
            '.fh5': 'image/x-freehand',
            '.fh7': 'image/x-freehand',
            '.fhc': 'image/x-freehand',
            '.fig': 'application/x-xfig',
            '.fli': 'video/x-fli',
            '.flo': 'application/vnd.micrografx.flo',
            '.flv': 'video/x-flv',
            '.flw': 'application/vnd.kde.kivio',
            '.flx': 'text/vnd.fmi.flexstor',
            '.fly': 'text/vnd.fly',
            '.fm': 'application/vnd.framemaker',
            '.fnc': 'application/vnd.frogans.fnc',
            '.for': 'text/x-fortran',
            '.fpx': 'image/vnd.fpx',
            '.frame': 'application/vnd.framemaker',
            '.fsc': 'application/vnd.fsc.weblaunch',
            '.fst': 'image/vnd.fst',
            '.ftc': 'application/vnd.fluxtime.clip',
            '.fti': 'application/vnd.anser-web-funds-transfer-initiation',
            '.fvt': 'video/vnd.fvt',
            '.fzs': 'application/vnd.fuzzysheet',
            '.g3': 'image/g3fax',
            '.gac': 'application/vnd.groove-account',
            '.gdl': 'model/vnd.gdl',
            '.geo': 'application/vnd.dynageo',
            '.gex': 'application/vnd.geometry-explorer',
            '.ggb': 'application/vnd.geogebra.file',
            '.ggt': 'application/vnd.geogebra.tool',
            '.ghf': 'application/vnd.groove-help',
            '.gif': 'image/gif',
            '.gim': 'application/vnd.groove-identity-message',
            '.gmx': 'application/vnd.gmx',
            '.gnumeric': 'application/x-gnumeric',
            '.gph': 'application/vnd.flographit',
            '.gqf': 'application/vnd.grafeq',
            '.gqs': 'application/vnd.grafeq',
            '.gram': 'application/srgs',
            '.gre': 'application/vnd.geometry-explorer',
            '.grv': 'application/vnd.groove-injector',
            '.grxml': 'application/srgs+xml',
            '.gsf': 'application/x-font-ghostscript',
            '.gtar': 'application/x-gtar',
            '.gtm': 'application/vnd.groove-tool-message',
            '.gtw': 'model/vnd.gtw',
            '.gv': 'text/vnd.graphviz',
            '.gz': 'application/x-gzip',
            '.h': 'text/x-c',
            '.h261': 'video/h261',
            '.h263': 'video/h263',
            '.h264': 'video/h264',
            '.hbci': 'application/vnd.hbci',
            '.hdf': 'application/x-hdf',
            '.hh': 'text/x-c',
            '.hlp': 'application/winhlp',
            '.hpgl': 'application/vnd.hp-hpgl',
            '.hpid': 'application/vnd.hp-hpid',
            '.hps': 'application/vnd.hp-hps',
            '.hqx': 'application/mac-binhex40',
            '.htke': 'application/vnd.kenameaapp',
            '.htm': 'text/html',
            '.html': 'text/html',
            '.hvd': 'application/vnd.yamaha.hv-dic',
            '.hvp': 'application/vnd.yamaha.hv-voice',
            '.hvs': 'application/vnd.yamaha.hv-script',
            '.icc': 'application/vnd.iccprofile',
            '.ice': 'x-conference/x-cooltalk',
            '.icm': 'application/vnd.iccprofile',
            '.ico': 'image/x-icon',
            '.ics': 'text/calendar',
            '.ief': 'image/ief',
            '.ifb': 'text/calendar',
            '.ifm': 'application/vnd.shana.informed.formdata',
            '.iges': 'model/iges',
            '.igl': 'application/vnd.igloader',
            '.igs': 'model/iges',
            '.igx': 'application/vnd.micrografx.igx',
            '.iif': 'application/vnd.shana.informed.interchange',
            '.imp': 'application/vnd.accpac.simply.imp',
            '.ims': 'application/vnd.ms-ims',
            '.in': 'text/plain',
            '.ipk': 'application/vnd.shana.informed.package',
            '.irm': 'application/vnd.ibm.rights-management',
            '.irp': 'application/vnd.irepository.package+xml',
            '.iso': 'application/octet-stream',
            '.itp': 'application/vnd.shana.informed.formtemplate',
            '.ivp': 'application/vnd.immervision-ivp',
            '.ivu': 'application/vnd.immervision-ivu',
            '.jad': 'text/vnd.sun.j2me.app-descriptor',
            '.jam': 'application/vnd.jam',
            '.jar': 'application/java-archive',
            '.java': 'text/x-java-source',
            '.jisp': 'application/vnd.jisp',
            '.jlt': 'application/vnd.hp-jlyt',
            '.jnlp': 'application/x-java-jnlp-file',
            '.joda': 'application/vnd.joost.joda-archive',
            '.jpe': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.jpg': 'image/jpeg',
            '.jpgm': 'video/jpm',
            '.jpgv': 'video/jpeg',
            '.jpm': 'video/jpm',
            '.js': 'application/javascript',
            '.json': 'application/json',
            '.kar': 'audio/midi',
            '.karbon': 'application/vnd.kde.karbon',
            '.kfo': 'application/vnd.kde.kformula',
            '.kia': 'application/vnd.kidspiration',
            '.kil': 'application/x-killustrator',
            '.kml': 'application/vnd.google-earth.kml+xml',
            '.kmz': 'application/vnd.google-earth.kmz',
            '.kne': 'application/vnd.kinar',
            '.knp': 'application/vnd.kinar',
            '.kon': 'application/vnd.kde.kontour',
            '.kpr': 'application/vnd.kde.kpresenter',
            '.kpt': 'application/vnd.kde.kpresenter',
            '.ksh': 'text/plain',
            '.ksp': 'application/vnd.kde.kspread',
            '.ktr': 'application/vnd.kahootz',
            '.ktz': 'application/vnd.kahootz',
            '.kwd': 'application/vnd.kde.kword',
            '.kwt': 'application/vnd.kde.kword',
            '.latex': 'application/x-latex',
            '.lbd': 'application/vnd.llamagraphics.life-balance.desktop',
            '.lbe': 'application/vnd.llamagraphics.life-balance.exchange+xml',
            '.les': 'application/vnd.hhe.lesson-player',
            '.lha': 'application/octet-stream',
            '.link66': 'application/vnd.route66.link66+xml',
            '.list': 'text/plain',
            '.list3820': 'application/vnd.ibm.modcap',
            '.listafp': 'application/vnd.ibm.modcap',
            '.log': 'text/plain',
            '.lostxml': 'application/lost+xml',
            '.lrf': 'application/octet-stream',
            '.lrm': 'application/vnd.ms-lrm',
            '.ltf': 'application/vnd.frogans.ltf',
            '.lvp': 'audio/vnd.lucent.voice',
            '.lwp': 'application/vnd.lotus-wordpro',
            '.lzh': 'application/octet-stream',
            '.m13': 'application/x-msmediaview',
            '.m14': 'application/x-msmediaview',
            '.m1v': 'video/mpeg',
            '.m2a': 'audio/mpeg',
            '.m2v': 'video/mpeg',
            '.m3a': 'audio/mpeg',
            '.m3u': 'audio/x-mpegurl',
            '.m4u': 'video/vnd.mpegurl',
            '.m4v': 'video/x-m4v',
            '.ma': 'application/mathematica',
            '.mag': 'application/vnd.ecowin.chart',
            '.maker': 'application/vnd.framemaker',
            '.man': 'text/troff',
            '.mathml': 'application/mathml+xml',
            '.mb': 'application/mathematica',
            '.mbk': 'application/vnd.mobius.mbk',
            '.mbox': 'application/mbox',
            '.mc1': 'application/vnd.medcalcdata',
            '.mcd': 'application/vnd.mcd',
            '.mcurl': 'text/vnd.curl.mcurl',
            '.mdb': 'application/x-msaccess',
            '.mdi': 'image/vnd.ms-modi',
            '.me': 'text/troff',
            '.mesh': 'model/mesh',
            '.mfm': 'application/vnd.mfmp',
            '.mgz': 'application/vnd.proteus.magazine',
            '.mht': 'message/rfc822',
            '.mhtml': 'message/rfc822',
            '.mid': 'audio/midi',
            '.midi': 'audio/midi',
            '.mif': 'application/vnd.mif',
            '.mime': 'message/rfc822',
            '.mj2': 'video/mj2',
            '.mjp2': 'video/mj2',
            '.mlp': 'application/vnd.dolby.mlp',
            '.mmd': 'application/vnd.chipnuts.karaoke-mmd',
            '.mmf': 'application/vnd.smaf',
            '.mmr': 'image/vnd.fujixerox.edmics-mmr',
            '.mny': 'application/x-msmoney',
            '.mobi': 'application/x-mobipocket-ebook',
            '.mov': 'video/quicktime',
            '.movie': 'video/x-sgi-movie',
            '.mp2': 'audio/mpeg',
            '.mp2a': 'audio/mpeg',
            '.mp3': 'audio/mpeg',
            '.mp4': 'video/mp4',
            '.mp4a': 'audio/mp4',
            '.mp4s': 'application/mp4',
            '.mp4v': 'video/mp4',
            '.mpa': 'video/mpeg',
            '.mpc': 'application/vnd.mophun.certificate',
            '.mpe': 'video/mpeg',
            '.mpeg': 'video/mpeg',
            '.mpg': 'video/mpeg',
            '.mpg4': 'video/mp4',
            '.mpga': 'audio/mpeg',
            '.mpkg': 'application/vnd.apple.installer+xml',
            '.mpm': 'application/vnd.blueice.multipass',
            '.mpn': 'application/vnd.mophun.application',
            '.mpp': 'application/vnd.ms-project',
            '.mpt': 'application/vnd.ms-project',
            '.mpy': 'application/vnd.ibm.minipay',
            '.mqy': 'application/vnd.mobius.mqy',
            '.mrc': 'application/marc',
            '.ms': 'text/troff',
            '.mscml': 'application/mediaservercontrol+xml',
            '.mseed': 'application/vnd.fdsn.mseed',
            '.mseq': 'application/vnd.mseq',
            '.msf': 'application/vnd.epson.msf',
            '.msh': 'model/mesh',
            '.msi': 'application/x-msdownload',
            '.msl': 'application/vnd.mobius.msl',
            '.msty': 'application/vnd.muvee.style',
            '.mts': 'model/vnd.mts',
            '.mus': 'application/vnd.musician',
            '.musicxml': 'application/vnd.recordare.musicxml+xml',
            '.mvb': 'application/x-msmediaview',
            '.mwf': 'application/vnd.mfer',
            '.mxf': 'application/mxf',
            '.mxl': 'application/vnd.recordare.musicxml',
            '.mxml': 'application/xv+xml',
            '.mxs': 'application/vnd.triscape.mxs',
            '.mxu': 'video/vnd.mpegurl',
            '.n-gage': 'application/vnd.nokia.n-gage.symbian.install',
            '.nb': 'application/mathematica',
            '.nc': 'application/x-netcdf',
            '.ncx': 'application/x-dtbncx+xml',
            '.ngdat': 'application/vnd.nokia.n-gage.data',
            '.nlu': 'application/vnd.neurolanguage.nlu',
            '.nml': 'application/vnd.enliven',
            '.nnd': 'application/vnd.noblenet-directory',
            '.nns': 'application/vnd.noblenet-sealer',
            '.nnw': 'application/vnd.noblenet-web',
            '.npx': 'image/vnd.net-fpx',
            '.nsf': 'application/vnd.lotus-notes',
            '.nws': 'message/rfc822',
            '.o': 'application/octet-stream',
            '.oa2': 'application/vnd.fujitsu.oasys2',
            '.oa3': 'application/vnd.fujitsu.oasys3',
            '.oas': 'application/vnd.fujitsu.oasys',
            '.obd': 'application/x-msbinder',
            '.obj': 'application/octet-stream',
            '.oda': 'application/oda',
            '.odb': 'application/vnd.oasis.opendocument.database',
            '.odc': 'application/vnd.oasis.opendocument.chart',
            '.odf': 'application/vnd.oasis.opendocument.formula',
            '.odft': 'application/vnd.oasis.opendocument.formula-template',
            '.odg': 'application/vnd.oasis.opendocument.graphics',
            '.odi': 'application/vnd.oasis.opendocument.image',
            '.odp': 'application/vnd.oasis.opendocument.presentation',
            '.ods': 'application/vnd.oasis.opendocument.spreadsheet',
            '.odt': 'application/vnd.oasis.opendocument.text',
            '.oga': 'audio/ogg',
            '.ogg': 'audio/ogg',
            '.ogv': 'video/ogg',
            '.ogx': 'application/ogg',
            '.onepkg': 'application/onenote',
            '.onetmp': 'application/onenote',
            '.onetoc': 'application/onenote',
            '.onetoc2': 'application/onenote',
            '.opf': 'application/oebps-package+xml',
            '.oprc': 'application/vnd.palm',
            '.org': 'application/vnd.lotus-organizer',
            '.osf': 'application/vnd.yamaha.openscoreformat',
            '.osfpvg': 'application/vnd.yamaha.openscoreformat.osfpvg+xml',
            '.otc': 'application/vnd.oasis.opendocument.chart-template',
            '.otf': 'application/x-font-otf',
            '.otg': 'application/vnd.oasis.opendocument.graphics-template',
            '.oth': 'application/vnd.oasis.opendocument.text-web',
            '.oti': 'application/vnd.oasis.opendocument.image-template',
            '.otm': 'application/vnd.oasis.opendocument.text-master',
            '.otp': 'application/vnd.oasis.opendocument.presentation-template',
            '.ots': 'application/vnd.oasis.opendocument.spreadsheet-template',
            '.ott': 'application/vnd.oasis.opendocument.text-template',
            '.oxt': 'application/vnd.openofficeorg.extension',
            '.p': 'text/x-pascal',
            '.p10': 'application/pkcs10',
            '.p12': 'application/x-pkcs12',
            '.p7b': 'application/x-pkcs7-certificates',
            '.p7c': 'application/pkcs7-mime',
            '.p7m': 'application/pkcs7-mime',
            '.p7r': 'application/x-pkcs7-certreqresp',
            '.p7s': 'application/pkcs7-signature',
            '.pas': 'text/x-pascal',
            '.pbd': 'application/vnd.powerbuilder6',
            '.pbm': 'image/x-portable-bitmap',
            '.pcf': 'application/x-font-pcf',
            '.pcl': 'application/vnd.hp-pcl',
            '.pclxl': 'application/vnd.hp-pclxl',
            '.pct': 'image/x-pict',
            '.pcurl': 'application/vnd.curl.pcurl',
            '.pcx': 'image/x-pcx',
            '.pdb': 'application/vnd.palm',
            '.pdf': 'application/pdf',
            '.pfa': 'application/x-font-type1',
            '.pfb': 'application/x-font-type1',
            '.pfm': 'application/x-font-type1',
            '.pfr': 'application/font-tdpfr',
            '.pfx': 'application/x-pkcs12',
            '.pgm': 'image/x-portable-graymap',
            '.pgn': 'application/x-chess-pgn',
            '.pgp': 'application/pgp-encrypted',
            '.pic': 'image/x-pict',
            '.pkg': 'application/octet-stream',
            '.pki': 'application/pkixcmp',
            '.pkipath': 'application/pkix-pkipath',
            '.pl': 'text/plain',
            '.plb': 'application/vnd.3gpp.pic-bw-large',
            '.plc': 'application/vnd.mobius.plc',
            '.plf': 'application/vnd.pocketlearn',
            '.pls': 'application/pls+xml',
            '.pml': 'application/vnd.ctc-posml',
            '.png': 'image/png',
            '.pnm': 'image/x-portable-anymap',
            '.portpkg': 'application/vnd.macports.portpkg',
            '.pot': 'application/vnd.ms-powerpoint',
            '.potm': 'application/vnd.ms-powerpoint.template.macroenabled.12',
            '.potx': 'application/vnd.openxmlformats-officedocument.presentationml.template',
            '.ppa': 'application/vnd.ms-powerpoint',
            '.ppam': 'application/vnd.ms-powerpoint.addin.macroenabled.12',
            '.ppd': 'application/vnd.cups-ppd',
            '.ppm': 'image/x-portable-pixmap',
            '.pps': 'application/vnd.ms-powerpoint',
            '.ppsm': 'application/vnd.ms-powerpoint.slideshow.macroenabled.12',
            '.ppsx': 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
            '.ppt': 'application/vnd.ms-powerpoint',
            '.pptm': 'application/vnd.ms-powerpoint.presentation.macroenabled.12',
            '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            '.pqa': 'application/vnd.palm',
            '.prc': 'application/x-mobipocket-ebook',
            '.pre': 'application/vnd.lotus-freelance',
            '.prf': 'application/pics-rules',
            '.ps': 'application/postscript',
            '.psb': 'application/vnd.3gpp.pic-bw-small',
            '.psd': 'image/vnd.adobe.photoshop',
            '.psf': 'application/x-font-linux-psf',
            '.ptid': 'application/vnd.pvi.ptid1',
            '.pub': 'application/x-mspublisher',
            '.pvb': 'application/vnd.3gpp.pic-bw-var',
            '.pwn': 'application/vnd.3m.post-it-notes',
            '.pwz': 'application/vnd.ms-powerpoint',
            '.py': 'text/x-python',
            '.pya': 'audio/vnd.ms-playready.media.pya',
            '.pyc': 'application/x-python-code',
            '.pyo': 'application/x-python-code',
            '.pyv': 'video/vnd.ms-playready.media.pyv',
            '.qam': 'application/vnd.epson.quickanime',
            '.qbo': 'application/vnd.intu.qbo',
            '.qfx': 'application/vnd.intu.qfx',
            '.qps': 'application/vnd.publishare-delta-tree',
            '.qt': 'video/quicktime',
            '.qwd': 'application/vnd.quark.quarkxpress',
            '.qwt': 'application/vnd.quark.quarkxpress',
            '.qxb': 'application/vnd.quark.quarkxpress',
            '.qxd': 'application/vnd.quark.quarkxpress',
            '.qxl': 'application/vnd.quark.quarkxpress',
            '.qxt': 'application/vnd.quark.quarkxpress',
            '.ra': 'audio/x-pn-realaudio',
            '.ram': 'audio/x-pn-realaudio',
            '.rar': 'application/x-rar-compressed',
            '.ras': 'image/x-cmu-raster',
            '.rcprofile': 'application/vnd.ipunplugged.rcprofile',
            '.rdf': 'application/rdf+xml',
            '.rdz': 'application/vnd.data-vision.rdz',
            '.rep': 'application/vnd.businessobjects',
            '.res': 'application/x-dtbresource+xml',
            '.rgb': 'image/x-rgb',
            '.rif': 'application/reginfo+xml',
            '.rl': 'application/resource-lists+xml',
            '.rlc': 'image/vnd.fujixerox.edmics-rlc',
            '.rld': 'application/resource-lists-diff+xml',
            '.rm': 'application/vnd.rn-realmedia',
            '.rmi': 'audio/midi',
            '.rmp': 'audio/x-pn-realaudio-plugin',
            '.rms': 'application/vnd.jcp.javame.midlet-rms',
            '.rnc': 'application/relax-ng-compact-syntax',
            '.roff': 'text/troff',
            '.rpm': 'application/x-rpm',
            '.rpss': 'application/vnd.nokia.radio-presets',
            '.rpst': 'application/vnd.nokia.radio-preset',
            '.rq': 'application/sparql-query',
            '.rs': 'application/rls-services+xml',
            '.rsd': 'application/rsd+xml',
            '.rss': 'application/rss+xml',
            '.rtf': 'text/rtf',
            '.rtx': 'text/richtext',
            '.s': 'text/x-asm',
            '.saf': 'application/vnd.yamaha.smaf-audio',
            '.sbml': 'application/sbml+xml',
            '.sc': 'application/vnd.ibm.secure-container',
            '.scd': 'application/x-msschedule',
            '.scm': 'application/vnd.lotus-screencam',
            '.scq': 'application/scvp-cv-request',
            '.scs': 'application/scvp-cv-response',
            '.scurl': 'text/vnd.curl.scurl',
            '.sda': 'application/vnd.stardivision.draw',
            '.sdc': 'application/vnd.stardivision.calc',
            '.sdd': 'application/vnd.stardivision.impress',
            '.sdkd': 'application/vnd.solent.sdkm+xml',
            '.sdkm': 'application/vnd.solent.sdkm+xml',
            '.sdp': 'application/sdp',
            '.sdw': 'application/vnd.stardivision.writer',
            '.see': 'application/vnd.seemail',
            '.seed': 'application/vnd.fdsn.seed',
            '.sema': 'application/vnd.sema',
            '.semd': 'application/vnd.semd',
            '.semf': 'application/vnd.semf',
            '.ser': 'application/java-serialized-object',
            '.setpay': 'application/set-payment-initiation',
            '.setreg': 'application/set-registration-initiation',
            '.sfd-hdstx': 'application/vnd.hydrostatix.sof-data',
            '.sfs': 'application/vnd.spotfire.sfs',
            '.sgl': 'application/vnd.stardivision.writer-global',
            '.sgm': 'text/sgml',
            '.sgml': 'text/sgml',
            '.sh': 'application/x-sh',
            '.shar': 'application/x-shar',
            '.shf': 'application/shf+xml',
            '.si': 'text/vnd.wap.si',
            '.sic': 'application/vnd.wap.sic',
            '.sig': 'application/pgp-signature',
            '.silo': 'model/mesh',
            '.sis': 'application/vnd.symbian.install',
            '.sisx': 'application/vnd.symbian.install',
            '.sit': 'application/x-stuffit',
            '.sitx': 'application/x-stuffitx',
            '.skd': 'application/vnd.koan',
            '.skm': 'application/vnd.koan',
            '.skp': 'application/vnd.koan',
            '.skt': 'application/vnd.koan',
            '.sl': 'text/vnd.wap.sl',
            '.slc': 'application/vnd.wap.slc',
            '.sldm': 'application/vnd.ms-powerpoint.slide.macroenabled.12',
            '.sldx': 'application/vnd.openxmlformats-officedocument.presentationml.slide',
            '.slt': 'application/vnd.epson.salt',
            '.smf': 'application/vnd.stardivision.math',
            '.smi': 'application/smil+xml',
            '.smil': 'application/smil+xml',
            '.snd': 'audio/basic',
            '.snf': 'application/x-font-snf',
            '.so': 'application/octet-stream',
            '.spc': 'application/x-pkcs7-certificates',
            '.spf': 'application/vnd.yamaha.smaf-phrase',
            '.spl': 'application/x-futuresplash',
            '.spot': 'text/vnd.in3d.spot',
            '.spp': 'application/scvp-vp-response',
            '.spq': 'application/scvp-vp-request',
            '.spx': 'audio/ogg',
            '.src': 'application/x-wais-source',
            '.srx': 'application/sparql-results+xml',
            '.sse': 'application/vnd.kodak-descriptor',
            '.ssf': 'application/vnd.epson.ssf',
            '.ssml': 'application/ssml+xml',
            '.stc': 'application/vnd.sun.xml.calc.template',
            '.std': 'application/vnd.sun.xml.draw.template',
            '.stf': 'application/vnd.wt.stf',
            '.sti': 'application/vnd.sun.xml.impress.template',
            '.stk': 'application/hyperstudio',
            '.stl': 'application/vnd.ms-pki.stl',
            '.str': 'application/vnd.pg.format',
            '.stw': 'application/vnd.sun.xml.writer.template',
            '.sus': 'application/vnd.sus-calendar',
            '.susp': 'application/vnd.sus-calendar',
            '.sv4cpio': 'application/x-sv4cpio',
            '.sv4crc': 'application/x-sv4crc',
            '.svd': 'application/vnd.svd',
            '.svg': 'image/svg+xml',
            '.svgz': 'image/svg+xml',
            '.swa': 'application/x-director',
            '.swf': 'application/x-shockwave-flash',
            '.swi': 'application/vnd.arastra.swi',
            '.sxc': 'application/vnd.sun.xml.calc',
            '.sxd': 'application/vnd.sun.xml.draw',
            '.sxg': 'application/vnd.sun.xml.writer.global',
            '.sxi': 'application/vnd.sun.xml.impress',
            '.sxm': 'application/vnd.sun.xml.math',
            '.sxw': 'application/vnd.sun.xml.writer',
            '.t': 'text/troff',
            '.tao': 'application/vnd.tao.intent-module-archive',
            '.tar': 'application/x-tar',
            '.tcap': 'application/vnd.3gpp2.tcap',
            '.tcl': 'application/x-tcl',
            '.teacher': 'application/vnd.smart.teacher',
            '.tex': 'application/x-tex',
            '.texi': 'application/x-texinfo',
            '.texinfo': 'application/x-texinfo',
            '.text': 'text/plain',
            '.tfm': 'application/x-tex-tfm',
            '.tgz': 'application/x-gzip',
            '.tif': 'image/tiff',
            '.tiff': 'image/tiff',
            '.tmo': 'application/vnd.tmobile-livetv',
            '.torrent': 'application/x-bittorrent',
            '.tpl': 'application/vnd.groove-tool-template',
            '.tpt': 'application/vnd.trid.tpt',
            '.tr': 'text/troff',
            '.tra': 'application/vnd.trueapp',
            '.trm': 'application/x-msterminal',
            '.tsv': 'text/tab-separated-values',
            '.ttc': 'application/x-font-ttf',
            '.ttf': 'application/x-font-ttf',
            '.twd': 'application/vnd.simtech-mindmapper',
            '.twds': 'application/vnd.simtech-mindmapper',
            '.txd': 'application/vnd.genomatix.tuxedo',
            '.txf': 'application/vnd.mobius.txf',
            '.txt': 'text/plain',
            '.u32': 'application/x-authorware-bin',
            '.udeb': 'application/x-debian-package',
            '.ufd': 'application/vnd.ufdl',
            '.ufdl': 'application/vnd.ufdl',
            '.umj': 'application/vnd.umajin',
            '.unityweb': 'application/vnd.unity',
            '.uoml': 'application/vnd.uoml+xml',
            '.uri': 'text/uri-list',
            '.uris': 'text/uri-list',
            '.urls': 'text/uri-list',
            '.ustar': 'application/x-ustar',
            '.utz': 'application/vnd.uiq.theme',
            '.uu': 'text/x-uuencode',
            '.vcd': 'application/x-cdlink',
            '.vcf': 'text/x-vcard',
            '.vcg': 'application/vnd.groove-vcard',
            '.vcs': 'text/x-vcalendar',
            '.vcx': 'application/vnd.vcx',
            '.vis': 'application/vnd.visionary',
            '.viv': 'video/vnd.vivo',
            '.vor': 'application/vnd.stardivision.writer',
            '.vox': 'application/x-authorware-bin',
            '.vrml': 'model/vrml',
            '.vsd': 'application/vnd.visio',
            '.vsf': 'application/vnd.vsf',
            '.vss': 'application/vnd.visio',
            '.vst': 'application/vnd.visio',
            '.vsw': 'application/vnd.visio',
            '.vtu': 'model/vnd.vtu',
            '.vxml': 'application/voicexml+xml',
            '.w3d': 'application/x-director',
            '.wad': 'application/x-doom',
            '.wav': 'audio/x-wav',
            '.wax': 'audio/x-ms-wax',
            '.wbmp': 'image/vnd.wap.wbmp',
            '.wbs': 'application/vnd.criticaltools.wbs+xml',
            '.wbxml': 'application/vnd.wap.wbxml',
            '.wcm': 'application/vnd.ms-works',
            '.wdb': 'application/vnd.ms-works',
            '.wiz': 'application/msword',
            '.wks': 'application/vnd.ms-works',
            '.wm': 'video/x-ms-wm',
            '.wma': 'audio/x-ms-wma',
            '.wmd': 'application/x-ms-wmd',
            '.wmf': 'application/x-msmetafile',
            '.wml': 'text/vnd.wap.wml',
            '.wmlc': 'application/vnd.wap.wmlc',
            '.wmls': 'text/vnd.wap.wmlscript',
            '.wmlsc': 'application/vnd.wap.wmlscriptc',
            '.wmv': 'video/x-ms-wmv',
            '.wmx': 'video/x-ms-wmx',
            '.wmz': 'application/x-ms-wmz',
            '.wpd': 'application/vnd.wordperfect',
            '.wpl': 'application/vnd.ms-wpl',
            '.wps': 'application/vnd.ms-works',
            '.wqd': 'application/vnd.wqd',
            '.wri': 'application/x-mswrite',
            '.wrl': 'model/vrml',
            '.wsdl': 'application/wsdl+xml',
            '.wspolicy': 'application/wspolicy+xml',
            '.wtb': 'application/vnd.webturbo',
            '.wvx': 'video/x-ms-wvx',
            '.x32': 'application/x-authorware-bin',
            '.x3d': 'application/vnd.hzn-3d-crossword',
            '.xap': 'application/x-silverlight-app',
            '.xar': 'application/vnd.xara',
            '.xbap': 'application/x-ms-xbap',
            '.xbd': 'application/vnd.fujixerox.docuworks.binder',
            '.xbm': 'image/x-xbitmap',
            '.xdm': 'application/vnd.syncml.dm+xml',
            '.xdp': 'application/vnd.adobe.xdp+xml',
            '.xdw': 'application/vnd.fujixerox.docuworks',
            '.xenc': 'application/xenc+xml',
            '.xer': 'application/patch-ops-error+xml',
            '.xfdf': 'application/vnd.adobe.xfdf',
            '.xfdl': 'application/vnd.xfdl',
            '.xht': 'application/xhtml+xml',
            '.xhtml': 'application/xhtml+xml',
            '.xhvml': 'application/xv+xml',
            '.xif': 'image/vnd.xiff',
            '.xla': 'application/vnd.ms-excel',
            '.xlam': 'application/vnd.ms-excel.addin.macroenabled.12',
            '.xlb': 'application/vnd.ms-excel',
            '.xlc': 'application/vnd.ms-excel',
            '.xlm': 'application/vnd.ms-excel',
            '.xls': 'application/vnd.ms-excel',
            '.xlsb': 'application/vnd.ms-excel.sheet.binary.macroenabled.12',
            '.xlsm': 'application/vnd.ms-excel.sheet.macroenabled.12',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.xlt': 'application/vnd.ms-excel',
            '.xltm': 'application/vnd.ms-excel.template.macroenabled.12',
            '.xltx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
            '.xlw': 'application/vnd.ms-excel',
            '.xml': 'application/xml',
            '.xo': 'application/vnd.olpc-sugar',
            '.xop': 'application/xop+xml',
            '.xpdl': 'application/xml',
            '.xpi': 'application/x-xpinstall',
            '.xpm': 'image/x-xpixmap',
            '.xpr': 'application/vnd.is-xpr',
            '.xps': 'application/vnd.ms-xpsdocument',
            '.xpw': 'application/vnd.intercon.formnet',
            '.xpx': 'application/vnd.intercon.formnet',
            '.xsl': 'application/xml',
            '.xslt': 'application/xslt+xml',
            '.xsm': 'application/vnd.syncml+xml',
            '.xspf': 'application/xspf+xml',
            '.xul': 'application/vnd.mozilla.xul+xml',
            '.xvm': 'application/xv+xml',
            '.xvml': 'application/xv+xml',
            '.xwd': 'image/x-xwindowdump',
            '.xyz': 'chemical/x-xyz',
            '.zaz': 'application/vnd.zzazz.deck+xml',
            '.zip': 'application/zip',
            '.zir': 'application/vnd.zul',
            '.zirz': 'application/vnd.zul',
            '.zmm': 'application/vnd.handheld-entertainment+xml'
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: FileUploaderBase, deps: [{ token: i1.HttpClient }, { token: i2.TranslateService }, { token: BaseAlertService }, { token: i4.Platform }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: FileUploaderBase, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: FileUploaderBase, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: i2.TranslateService }, { type: BaseAlertService }, { type: i4.Platform }] });

class FileUploaderService extends FileUploaderBase {
    // private fileAlertLoader: HTMLIonAlertElement;
    testToken = '1000.dc0ce841f5fab8681de0b67411276c34.deda89ba9a3eda041838c0719fc4c023';
    ticketId = '49185000001789001';
    filePreview;
    constructor(http, translateService, alertService, 
    // @Inject(ENVIRONMENT) env: Environment,
    plt) {
        super(http, translateService, alertService, plt);
        this.fillInFileExtentions();
    }
    get isFileAttached() {
        return this.fileToUpload !== null;
    }
    get fileName() {
        return this.fileNameToUpload;
    }
    addFileZohoTicket($event, ticketId) {
        this.ticketId = ticketId;
        super.addFile($event)
            .pipe(switchMap(({ file, validateFile }) => this.handleErrorsOnAddingFile(validateFile, file)))
            .subscribe();
    }
    addFileZohoReplay($event, ticketId, objectType, type = AlertContentType.Progress, uploadUrl = `${this.API_URL}/net/chat/file_upload`) {
        this.ticketId = ticketId;
        super.addFile($event, type).pipe(switchMap(({ file, validateFile }) => this.doPreview(file, validateFile, type)
            .pipe(switchMap(() => this.handleErrorsOnAddingFile(validateFile, $event.target.files[0])), switchMap(() => this.presentAlertForPreview(file, validateFile, type, {
            uploadHandlerFunc: this.uploadHandlerFunc(type, objectType, uploadUrl)
        }))))).subscribe();
    }
    uploadHandlerFunc = (type, objectType, url = null) => {
        return () => {
            this.uploadFileZoho({
                ticketId: this.ticketId,
                isZoho: objectType === 'claims',
                url
            }, type)
                .subscribe(() => {
            }, error => {
                this.updateAlertContent(AlertContentType.Error, {
                    errorType: FileUploadingErrorType.Unknown
                });
            });
        };
    };
    uploadFileZoho(options, type = AlertContentType.Progress) {
        if (!this.fileToUpload) {
            return of({ state: 'DONE' });
        }
        return this.preloadLoadingData(this.fileToUpload)
            .pipe(switchMap((preloadData) => {
            return this.createAlert(preloadData, type);
        }), switchMap(() => {
            return options?.isZoho ? this._uploadFileZoho(options.ticketId) : this.uploadFile(options.url);
        }), tap((upload) => {
            this.uploadProgress = upload;
            // console.log('this.uploadProgress', this.uploadProgress);
            if (this?.uploadProgress?.state === 'DONE') {
                // console.log('uploadFileZoho - upload', this.uploadProgress);
                this.uploadingSubject.next({
                    reqType: 'upload', fileEventTypes: FileEventTypes.Completed,
                    data: upload?.data || null
                });
                this.unsubscribe();
            }
            else {
                this.updateAlertContent(AlertContentType.Progress, {
                    loaded: upload.loaded,
                    total: upload.total,
                    progress: upload.progress
                });
            }
        }));
    }
    createAlert(preloadData, type) {
        this.fileNameToUpload = preloadData.name;
        if (type === 'progress') {
            return this.presentAlert({
                loaded: '0',
                total: preloadData.total,
                progress: 1,
                uploadHandlerFunc: this.uploadHandlerFunc(type, 'claims', null)
            }, AlertContentType.Progress);
        }
        else {
            return of([]);
        }
    }
    _uploadFileZoho(ticketId) {
        if (!this.fileToUpload) {
            return of([]);
        }
        const url = `/api/v1/tickets/${ticketId}/attachments`;
        const formData = new FormData();
        formData.append('file', this.fileToUpload);
        const customHeaders = {};
        const a = 'Authorization';
        customHeaders[a] = `Zoho-oauthtoken ${this.testToken}`;
        return this.post(url, formData, 'file', true)
            .pipe(takeUntil(this.notifier), scan((u, e) => {
            return this.calcState(u, e, null);
        }, this.getInitialState()));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: FileUploaderService, deps: [{ token: i1.HttpClient }, { token: i2.TranslateService }, { token: BaseAlertService }, { token: i4.Platform }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: FileUploaderService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: FileUploaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: i2.TranslateService }, { type: BaseAlertService }, { type: i4.Platform }] });

var AlertType;
(function (AlertType) {
    AlertType[AlertType["RevokeAccess"] = 0] = "RevokeAccess";
    AlertType[AlertType["StopAccess"] = 1] = "StopAccess";
    AlertType[AlertType["OpenDoor"] = 2] = "OpenDoor";
    AlertType[AlertType["ShareWithEmailAlreadyShared"] = 3] = "ShareWithEmailAlreadyShared";
    AlertType[AlertType["BookingOwner"] = 4] = "BookingOwner";
    AlertType[AlertType["Default"] = 5] = "Default";
    AlertType[AlertType["OpenDoorShareInactive"] = 6] = "OpenDoorShareInactive";
    AlertType[AlertType["TerminateWithShares"] = 7] = "TerminateWithShares";
    AlertType[AlertType["SkipCardAdding"] = 8] = "SkipCardAdding";
    AlertType[AlertType["AccountACannotBeUsed"] = 9] = "AccountACannotBeUsed";
    AlertType[AlertType["EmailNotFound"] = 10] = "EmailNotFound";
    AlertType[AlertType["DataIsIncomplete"] = 11] = "DataIsIncomplete";
    AlertType[AlertType["ForgetToCloseTheDoors"] = 12] = "ForgetToCloseTheDoors";
})(AlertType || (AlertType = {}));
var NotificationType;
(function (NotificationType) {
    NotificationType["BOX_IS_OPEN_TOO_LONG"] = "BOX_IS_OPEN_TOO_LONG";
    NotificationType["MSP_ORDER"] = "MSP_ORDER";
    NotificationType["MANUAL"] = "MANUAL";
    NotificationType["NEWS"] = "NEWS";
})(NotificationType || (NotificationType = {}));
var PaymentMethodEnum;
(function (PaymentMethodEnum) {
    PaymentMethodEnum["STRIPE_CARD"] = "STRIPE_CARD";
    PaymentMethodEnum["STRIPE_GIROPAY"] = "STRIPE_GIROPAY";
    PaymentMethodEnum["PAYPAL"] = "PAYPAL";
    PaymentMethodEnum["FREE_HOURS"] = "FREE_HOURS";
    PaymentMethodEnum["PAYREXX_TWINT"] = "PAYREXX_TWINT";
})(PaymentMethodEnum || (PaymentMethodEnum = {}));

class AlertService extends BaseAlertService {
    alertController;
    router;
    translate;
    constructor(alertController, router, translate) {
        super(alertController);
        this.alertController = alertController;
        this.router = router;
        this.translate = translate;
    }
    async showForgotPasswordAlert(urlToRedirect) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant('buttons.forgot-password'),
            message: this.translate.instant('alerts.contact-admin'),
            buttons: [
                {
                    text: this.translate.instant('buttons.continue'),
                    // cssClass: 'secondary',
                    handler: (blah) => {
                        // this.router.navigate([this.appRoutes.info.contactUs.fullPath]);
                        this.router.navigate([urlToRedirect]);
                    }
                }
            ]
        });
        await alert.present();
    }
    async noAvailableBoxes() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant('alerts.title'),
            message: this.translate.instant('alerts.no-box'),
            buttons: [
                {
                    text: this.translate.instant('buttons.continue'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async distanceAlert(type, distance) {
        let fullMessage = '';
        if (type === 'distance-close') {
            fullMessage = `${this.translate.instant('alerts.distance-close')}
			 ${distance}m ${this.translate.instant('alerts.distance-close2')}`;
        }
        else {
            fullMessage = `${this.translate.instant('alerts.distance-open')}
			 ${distance}m ${this.translate.instant('alerts.distance-open2')}`;
        }
        const alert = await this.alertController.create({
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: fullMessage,
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.back'),
                    role: 'cancel',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async terminate(onTerminate, onKeepBooking) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.attention'),
            mode: 'ios',
            message: this.translate.instant('alerts.you-are-about-terminating'),
            backdropDismiss: false,
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.terminate-booking'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                        onTerminate();
                    }
                },
                {
                    text: this.translate.instant('booking-steps.buttons.keep-booking'),
                    // role: 'cancel',
                    cssClass: 'button-outline',
                    handler: () => {
                        onKeepBooking();
                    }
                }
            ]
        });
        await alert.present();
    }
    async cancel(onCancel, onKeepBooking) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.attention'),
            mode: 'ios',
            message: this.translate.instant('alerts.you-are-about-cancel'),
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.cancel-booking'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                        onCancel();
                    }
                },
                {
                    text: this.translate.instant('booking-steps.buttons.keep-booking'),
                    // role: 'cancel',
                    cssClass: 'button-outline',
                    handler: () => {
                        onKeepBooking();
                    }
                }
            ]
        });
        await alert.present();
    }
    async userNotExists() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: this.translate.instant('alerts.user-not-exists'),
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.back'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async somethingWentWrong() {
        console.log('somethingWentWrong');
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: this.translate.instant('error.interval-server-error'),
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.back'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async userWithEmailAlreadyExists() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: this.translate.instant('alerts.email-was-taken'),
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.back'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async provideFirstNameLastName(errors) {
        const translateKey = `alerts.provide-${errors.firstName && errors.lastName ? 'fullname' : errors.firstName ? 'firstname' : 'lastname'}`;
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: this.translate.instant(translateKey),
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.back'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async bookingCreated() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.booking-created-title'),
            mode: 'ios',
            message: this.translate.instant('alerts.booking-created-message'),
            buttons: [
                {
                    text: this.translate.instant('buttons.continue'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async emailPhoneNotVerified(onSuccess, onCancel) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant('alerts.attention'),
            message: this.translate.instant('terms-and-privacy.data-is-incomplete'),
            buttons: [
                {
                    text: this.translate.instant('alerts.open-profile'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                        onSuccess();
                    }
                },
                {
                    text: this.translate.instant('buttons.cancel'),
                    // role: 'cancel',
                    cssClass: 'button-outline',
                    handler: () => {
                        onCancel();
                    }
                }
            ]
        });
        await alert.present();
    }
    async noAvailableBoxesForSubscription() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: this.translate.instant('alerts.no-boxes-for-subscription'),
            buttons: [
                {
                    text: this.translate.instant('booking-steps.buttons.back'),
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async noAvailableSubscriptions() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: this.translate.instant('alerts.title'),
            mode: 'ios',
            message: this.translate.instant('alerts.subscriptions-no-available'),
            buttons: [
                {
                    text: this.translate.instant('buttons.continue'),
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async deleteCard(type, onSuccess, onCancel) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant('alerts.attention'),
            message: this.translate.instant(type === 'STRIPE_CARD' ? 'alerts.delete-the-card' : 'alerts.delete-paypal'),
            buttons: [
                {
                    text: this.translate.instant('buttons.yes'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                        onSuccess();
                    }
                },
                {
                    text: this.translate.instant('buttons.cancel'),
                    // role: 'cancel',
                    cssClass: 'button-outline',
                    handler: () => {
                        onCancel();
                    }
                }
            ]
        });
        await alert.present();
    }
    async wishTowerInformation(onSuccess, onCancel) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            message: this.translate.instant('groups.map.information-message'),
            buttons: [
                {
                    text: this.translate.instant('groups.map.got-it'),
                    handler: () => {
                        onSuccess();
                    }
                },
                {
                    text: this.translate.instant('groups.map.do-not-show'),
                    role: 'cancel',
                    cssClass: 'button-outline',
                    handler: () => {
                        onCancel();
                    }
                }
            ]
        });
        await alert.present();
    }
    async wishTower(onSuccess, onCancel) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            // header: this.translate.instant('alerts.attention'),
            message: this.translate.instant('groups.map.vote-for-a-tower'),
            buttons: [
                {
                    text: this.translate.instant('buttons.yes'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                        onSuccess();
                    }
                },
                {
                    text: this.translate.instant('buttons.cancel'),
                    role: 'cancel',
                    cssClass: 'button-outline',
                    handler: () => {
                        console.log('onCancel');
                        onCancel();
                    }
                }
            ]
        });
        await alert.present();
    }
    async wishTowerSuccess() {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            // header: this.translate.instant('alerts.attention'),
            message: this.translate.instant('groups.map.vote-a-tower-success'),
            buttons: [
                {
                    text: this.translate.instant('buttons.yes'),
                    role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                    }
                }
            ]
        });
        await alert.present();
    }
    async locationDisabled(onSuccess, onCancel) {
        console.log('locationDisabled');
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant('permissions.location-title'),
            message: this.translate.instant('permissions.location-message'),
            buttons: [
                {
                    text: this.translate.instant('permissions.open-settings'),
                    // role: 'cancel',
                    // cssClass: 'secondary',
                    handler: () => {
                        onSuccess();
                    }
                },
                {
                    text: this.translate.instant('permissions.set-up-later'),
                    role: 'cancel',
                    cssClass: 'button-outline',
                    // cssClass: 'secondary',
                    handler: () => {
                        onCancel();
                    }
                }
            ]
        });
        await alert.present();
    }
    async oneButtonsAlert(onSuccess, alertType, message = null, params = null) {
        const alertObj = this.buildAlert(alertType, params);
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant(alertObj.header),
            message: message || this.translate.instant(alertObj.message || alertObj.fullMessage),
            buttons: [
                {
                    text: this.translate.instant('buttons.ok'),
                    handler: () => {
                        onSuccess();
                    }
                }
            ]
        });
        await alert.present();
    }
    async oneButtonsAlertFromPush(type, onSuccess, message, title, showContinueBtn) {
        const buttons = [];
        if (showContinueBtn) {
            buttons.push({
                text: this.translate.instant(type === NotificationType.NEWS ? 'new-block.read-more' : type === NotificationType.MSP_ORDER ? 'buttons.open-order' : 'buttons.continue'),
                handler: () => {
                    onSuccess();
                }
            });
        }
        buttons.push({
            text: this.translate.instant('buttons.close'),
            // role: 'cancel',
            cssClass: 'button-outline',
            handler: () => {
            }
        });
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            header: title,
            mode: 'ios',
            message,
            backdropDismiss: false,
            buttons
        });
        await alert.present();
    }
    async twoButtonsAlert(onSuccess, onCancel, alertType, params = null) {
        const alertObj = this.buildAlert(alertType);
        let createdAlert = {
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant(alertObj.header),
            message: this.translate.instant(alertObj.message),
            buttons: [
                {
                    text: this.translate.instant(alertObj.button1),
                    handler: () => {
                        onSuccess();
                    }
                },
                {
                    text: this.translate.instant(alertObj.button2),
                    cssClass: 'button-outline',
                    handler: () => {
                        onCancel();
                    }
                }
            ]
        };
        if (alertType === AlertType.TerminateWithShares) {
            const message = createdAlert.message;
            createdAlert = {
                ...createdAlert, ...{
                    message: message.replace('${activeCount}', params.activeCount)
                }
            };
        }
        const alert = await this.alertController.create(createdAlert);
        await alert.present();
    }
    async threeButtonsAlert(callBack1, callBack2, callBack3, alertType) {
        const alertObj = this.buildAlert(alertType);
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant(alertObj.header),
            message: this.translate.instant(alertObj.message),
            buttons: [
                {
                    text: this.translate.instant(alertObj.button1),
                    handler: () => {
                        callBack1();
                    }
                },
                {
                    text: this.translate.instant(alertObj.button2),
                    cssClass: 'button-outline',
                    handler: () => {
                        callBack2();
                    }
                },
                {
                    text: this.translate.instant(alertObj.button3),
                    cssClass: 'button-outline',
                    handler: () => {
                        callBack3();
                    }
                }
            ]
        });
        await alert.present();
    }
    async productAdded(urlToRedirect) {
        const alert = await this.alertController.create({
            cssClass: 'forgot-password-alert',
            mode: 'ios',
            header: this.translate.instant('alerts.booking-created-title'),
            message: this.translate.instant('alerts.product-added'),
            buttons: [
                {
                    text: this.translate.instant('product-details.go-to-cart'),
                    handler: (blah) => {
                        // this.router.navigate([this.appRoutes.shoppingCart.fullPath]);
                        this.router.navigate([urlToRedirect]);
                    }
                },
                {
                    text: this.translate.instant('buttons.continue-shopping'),
                    handler: (blah) => {
                    }
                }
            ]
        });
        await alert.present();
    }
    buildAlert(type, params = null) {
        let alert;
        switch (type) {
            case AlertType.RevokeAccess:
                alert = {
                    header: 'alerts.attention',
                    message: 'alerts.revoke-access',
                    button1: 'buttons.yes',
                    button2: 'buttons.cancel'
                };
                break;
            case AlertType.StopAccess:
                alert = {
                    header: 'alerts.attention',
                    message: 'alerts.stop-access',
                    button1: 'buttons.yes',
                    button2: 'buttons.cancel'
                };
                break;
            case AlertType.OpenDoor:
                alert = {
                    header: 'alerts.attention',
                    message: 'alerts.open-door',
                    button1: 'buttons.try-again',
                    button2: 'contact-us.contact-support',
                    button3: 'buttons.cancel',
                };
                break;
            case AlertType.ShareWithEmailAlreadyShared:
                alert = {
                    header: 'alerts.attention',
                    message: 'share-access.alerts.already-has-shared',
                    button1: 'buttons.try-again',
                };
                break;
            case AlertType.BookingOwner:
                alert = {
                    header: 'alerts.attention',
                    message: 'share-access.alerts.same-owner',
                    button1: 'buttons.try-again',
                };
                break;
            case AlertType.Default:
                alert = {
                    header: 'alerts.attention',
                    message: '',
                    button1: 'buttons.ok',
                };
                break;
            case AlertType.OpenDoorShareInactive:
                alert = {
                    header: 'alerts.attention',
                    message: 'share-access.alerts.your-share-is-inactive',
                    button1: 'buttons.ok',
                };
                break;
            case AlertType.TerminateWithShares:
                alert = {
                    header: 'alerts.attention',
                    message: 'share-access.alerts.terminate-booking-with-shares',
                    button1: 'buttons.ok',
                    button2: 'buttons.cancel'
                };
                break;
            case AlertType.SkipCardAdding:
                alert = {
                    header: 'alerts.attention',
                    message: 'payment.are-you-sure-to-skip',
                    button1: 'buttons.skip-anyway',
                    button2: 'buttons.go-back'
                };
                break;
            case AlertType.AccountACannotBeUsed:
                alert = {
                    message: 'social-login.account-cannot-be-use',
                    header: 'alerts.attention',
                    button1: 'buttons.skip-anyway',
                    button2: 'buttons.go-back'
                };
                break;
            case AlertType.EmailNotFound:
                alert = {
                    header: 'alerts.attention',
                    fullMessage: `${this.translate.instant('social-login.email-not-found-1')} ${params?.network || ''} ${this.translate.instant('social-login.email-not-found-2')}`,
                    button1: 'buttons.skip-anyway',
                    button2: 'buttons.go-back'
                };
                break;
            case AlertType.DataIsIncomplete:
                alert = {
                    header: 'alerts.attention',
                    message: 'terms-and-privacy.data-is-incomplete',
                    button1: 'terms-and-privacy.go-to-settings',
                    button2: 'buttons.close'
                };
                break;
        }
        return alert;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: AlertService, deps: [{ token: i4.AlertController }, { token: i2$1.Router }, { token: i2.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: AlertService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: AlertService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i4.AlertController }, { type: i2$1.Router }, { type: i2.TranslateService }] });

class VLockerCommonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: VLockerCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.5", ngImport: i0, type: VLockerCommonModule });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: VLockerCommonModule, providers: [
            FileUploaderService
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.5", ngImport: i0, type: VLockerCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    providers: [
                        FileUploaderService
                    ]
                }]
        }] });

/*
 * Public API Surface of v-locker-common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AlertContentType, AlertService, BaseAlertService, FileEventTypes, FileFormatTypes, FileUploaderBase, FileUploaderService, FileUploadingErrorType, VLockerCommonModule };
